import { useBitcoinAccount } from "@mezo-org/orangekit";
import { useGetCurrentAccount } from ".";
import { getCryptoAsset, isBitcoinLikeCryptoAsset, isUsdLikeCryptoAsset, } from "../utils/cryptoAssets";
import { formatNumberToCompactString, fromFixedPoint, fromFixedPointToString, } from "../utils/numbers";
import { useAssetsUsdConversion } from "./useAssetsUSDConversion";
import useWalletAccount from "./useWalletAccount";
import { formatUsd } from "../utils/currency";
/**
 * Formats crypto asset to balance details (balance in USD and decimals)
 * @param type The type of crypto asset
 * @param balance The balance of crypto asset
 * @param btcUsdConversionRate The USD conversion rate
 * @returns The balance details
 */
function formatCryptoAsset(type, balance, btcUsdConversionRate) {
    const { decimals } = getCryptoAsset(type);
    const nativeBalance = fromFixedPoint(balance, decimals);
    let balanceInUsd = 0;
    if (isBitcoinLikeCryptoAsset(type)) {
        balanceInUsd = fromFixedPoint(balance * BigInt(btcUsdConversionRate), decimals);
    }
    if (isUsdLikeCryptoAsset(type)) {
        balanceInUsd = nativeBalance;
    }
    return {
        type,
        balance: fromFixedPointToString(balance, decimals, isUsdLikeCryptoAsset(type) ? 2 : 4),
        balanceInUsd: formatUsd(balanceInUsd),
    };
}
/**
 * Aggregates dropdown data for Dropdown component.
 * @param options - Options for the dropdown data.
 * @param options.evmNativeAssets - Native assets for EVM wallet.
 * @param options.matsnetAssets - Matsnet tokens. Rendered as separate section.
 * @returns Dropdown data.
 * @dev This hook is for internal use only.
 */
export default function useDropdownData(options) {
    const { evmNativeAssets = [], matsnetAssets = [] } = options || {};
    const { btcBalance } = useBitcoinAccount();
    const { address, networkFamily, isConnected } = useWalletAccount();
    const { data: passportAccount } = useGetCurrentAccount();
    const { data: assetsUsdConversion } = useAssetsUsdConversion();
    if (!address || !isConnected || !passportAccount) {
        return null;
    }
    const mezoId = passportAccount.mezoId;
    const nativeAssets = networkFamily === "bitcoin"
        ? [
            {
                type: "btc",
                balance: BigInt(btcBalance?.total ?? 0),
            },
        ]
        : evmNativeAssets;
    const btcUsdConversionRate = Number(assetsUsdConversion?.btcUsd ?? 0);
    const formattedNativeAssets = nativeAssets.map((asset) => formatCryptoAsset(asset.type, asset.balance, btcUsdConversionRate));
    const totalBalanceInUsd = formatUsd(nativeAssets.reduce((sum, value) => {
        const { decimals } = getCryptoAsset(value.type);
        return (sum +
            fromFixedPoint(value.balance * BigInt(btcUsdConversionRate), decimals));
    }, 0));
    const matsBalance = formatNumberToCompactString(passportAccount.mats.totalMats, 2);
    const matsnetFormattedAssets = matsnetAssets.map((asset) => formatCryptoAsset(asset.type, asset.balance, btcUsdConversionRate));
    return {
        mezoId,
        address,
        walletType: networkFamily,
        totalBalanceInUsd,
        formattedNativeAssets,
        matsnet: {
            mats: matsBalance,
            formattedAssets: matsnetFormattedAssets,
        },
    };
}
//# sourceMappingURL=useDropdownData.js.map